import {drawTriangle,drawAngle,drawCoordinateGrid} from './extendedRenderers';
// Draws worksheet diagrams as SVG from the data the AI returns (see backend/functions/diagrams.js).
// Labels are set with textContent, never as markup, so nothing in a spec can inject HTML.
// Arrowheads are plain polygons rather than SVG markers so the PDF export (html2canvas) keeps them.

const SVG_NS = "http://www.w3.org/2000/svg";
const INK = "#1f2933";
const SHADE = "#c9d6e8";
const ACCENT = "#1f4fd1";
const FONT = "15px Arial, Helvetica, sans-serif";

function el(doc, name, attrs = {}, text) {
  const node = doc.createElementNS(SVG_NS, name);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
  if (text !== undefined) node.textContent = text;
  return node;
}

function svgRoot(doc, width, height) {
  return el(doc, "svg", {
    xmlns: SVG_NS,
    width,
    height,
    viewBox: `0 0 ${width} ${height}`,
    role: "img",
    // No margin here: html2canvas crops an SVG's own margin off its far side. The editor
    // block around it provides the spacing.
    style: `font: ${FONT}; display: block;`,
  });
}

// 3, 2.5, 0.25: no float noise such as 0.30000000000000004.
export function formatNumber(value) {
  return String(Number(value.toFixed(6)));
}

function arrowHead(doc, x, y, direction) {
  const d = direction === "right" ? 1 : -1;
  return el(doc, "polygon", {
    points: `${x},${y} ${x - 10 * d},${y - 6} ${x - 10 * d},${y + 6}`,
    fill: INK,
  });
}

function drawRectangle(doc, spec) {
  const maxWidth = 320;
  const maxHeight = 200;
  const scale = Math.min(maxWidth / spec.width, maxHeight / spec.height);
  // Keep very thin rectangles visible; labels carry the true measurements.
  const w = Math.max(spec.width * scale, 24);
  const h = Math.max(spec.height * scale, 24);
  const left = 90;
  const top = 12;
  const svg = svgRoot(doc, left + w + 24, top + h + 36);

  svg.appendChild(el(doc, "rect", { x: left, y: top, width: w, height: h, fill: "#ffffff", stroke: INK, "stroke-width": 2 }));

  if (spec.shaded) {
    const sx = w / spec.width;
    const sy = h / spec.height;
    svg.appendChild(el(doc, "rect", {
      x: left + spec.shaded.x * sx,
      y: top + spec.shaded.y * sy,
      width: spec.shaded.width * sx,
      height: spec.shaded.height * sy,
      fill: SHADE,
      stroke: INK,
      "stroke-width": 1,
    }));
  }

  const measure = (value) => `${formatNumber(value)} ${spec.unit}`.trim();
  svg.appendChild(el(doc, "text", { x: left + w / 2, y: top + h + 26, "text-anchor": "middle", fill: INK }, spec.widthLabel ?? measure(spec.width)));
  svg.appendChild(el(doc, "text", { x: left - 10, y: top + h / 2 + 5, "text-anchor": "end", fill: INK }, spec.heightLabel ?? measure(spec.height)));
  return svg;
}

function drawNumberLine(doc, spec) {
  const left = 30;
  const length = 480;
  const y = 44;
  const svg = svgRoot(doc, left * 2 + length, 84);
  const ticks = Math.floor((spec.max - spec.min) / spec.step + 1e-8);
  const x = (value) => left + ((value - spec.min) / (spec.max - spec.min)) * length;

  svg.appendChild(el(doc, "line", { x1: left - 16, y1: y, x2: left + length + 16, y2: y, stroke: INK, "stroke-width": 2 }));
  svg.appendChild(arrowHead(doc, left - 20, y, "left"));
  svg.appendChild(arrowHead(doc, left + length + 20, y, "right"));

  for (let i = 0; i <= ticks; i += 1) {
    const value = spec.min + i * spec.step;
    svg.appendChild(el(doc, "line", { x1: x(value), y1: y - 7, x2: x(value), y2: y + 7, stroke: INK, "stroke-width": 1.5 }));
    svg.appendChild(el(doc, "text", { x: x(value), y: y + 26, "text-anchor": "middle", fill: INK }, formatNumber(value)));
  }

  if (spec.highlight) {
    const from = spec.highlight.from ?? null;
    const to = spec.highlight.to ?? null;
    const x1 = from === null ? left - 14 : x(from);
    const x2 = to === null ? left + length + 14 : x(to);
    svg.appendChild(el(doc, "line", { x1, y1: y, x2, y2: y, stroke: ACCENT, "stroke-width": 5 }));
    if (from === null) svg.appendChild(el(doc, "polygon", { points: `${left - 26},${y} ${left - 12},${y - 8} ${left - 12},${y + 8}`, fill: ACCENT }));
    if (to === null) svg.appendChild(el(doc, "polygon", { points: `${left + length + 26},${y} ${left + length + 12},${y - 8} ${left + length + 12},${y + 8}`, fill: ACCENT }));
  }

  for (const point of spec.points || []) {
    const cx = x(point.value);
    svg.appendChild(el(doc, "circle", {
      cx,
      cy: y,
      r: 6.5,
      fill: point.style === "closed" ? ACCENT : "#ffffff",
      stroke: ACCENT,
      "stroke-width": 2.5,
    }));
    if (point.label) {
      svg.appendChild(el(doc, "text", { x: cx, y: y - 14, "text-anchor": "middle", fill: INK }, point.label));
    }
  }
  return svg;
}

const RENDERERS = {
  rectangle: drawRectangle,
  number_line: drawNumberLine,
  triangle: drawTriangle,
  angle: drawAngle,
  coordinate_grid: drawCoordinateGrid,
};

// Returns an <svg> element, or null when the spec isn't a diagram we can draw.
export function renderDiagram(spec, doc = document) {
  const draw = spec && RENDERERS[spec.kind];
  if (!draw) return null;
  if(spec.kind==='number_line'&&(!Number.isFinite(spec.step)||spec.step<=0||!Number.isFinite((spec.max-spec.min)/spec.step)||(spec.max-spec.min)/spec.step<=0||(spec.max-spec.min)/spec.step>20))return null;
  if(spec.kind==='rectangle'&&(!Number.isFinite(spec.width)||!Number.isFinite(spec.height)||spec.width<=0||spec.height<=0))return null;
  try {
    return draw(doc, spec);
  } catch {
    return null;
  }
}
